import React, { useState } from 'react';
import { ShoppingCart, Minus, Plus } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { imageProps } from '../lib/images';

const ProductCard = ({ product, onAddToCart }) => {
  const navigate = useNavigate();
  const [quantity, setQuantity] = useState(1);
  const installmentPrice = (Number(product.price || 0) / 3).toFixed(2);

  const handleCardClick = () => {
    navigate(`/produto/${product.id}`);
  };

  const handleAddToCart = (e) => {
    e.stopPropagation();
    onAddToCart(product, quantity);
  };

  const decrement = (e) => {
    e.stopPropagation();
    setQuantity((q) => Math.max(1, q - 1));
  };
  const increment = (e) => {
    e.stopPropagation();
    setQuantity((q) => q + 1);
  };

  return (
    <div
      onClick={handleCardClick}
      className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-3xl p-6 shadow-md hover:shadow-xl transition-all transform hover:-translate-y-2 relative cursor-pointer"
      data-testid={`product-card-${product.id}`}
    >
      {/* Discount Badge */}
      {product.discount && (
        <div className="absolute top-4 left-4 bg-orange-500 text-white text-xs font-bold px-3 py-1 rounded-full z-10">
          {product.discount} OFF
        </div>
      )}

      {/* New Badge */}
      {product.isNew && !product.discount && (
        <div className="absolute top-4 left-4 bg-blue-700 text-white text-xs font-bold px-3 py-1 rounded-full z-10">
          NOVIDADE!
        </div>
      )}

      {/* Product Image */}
      <div className="mb-4 flex items-center justify-center h-48 overflow-hidden rounded-2xl bg-white">
        <img
          {...imageProps(product.image, { sizes: '(max-width: 640px) 90vw, 300px' })}
          alt={product.name}
          loading="lazy"
          decoding="async"
          width="300"
          height="192"
          className="w-full h-full object-contain transform hover:scale-110 transition-transform duration-500"
        />
      </div>

      {/* Product Info */}
      <div className="text-center">
        <h3 className="text-lg font-bold text-gray-800 mb-2 min-h-[3rem]">
          {product.name}
        </h3>

        {/* Specs */}
        <div className="flex justify-center gap-2 mb-3 flex-wrap">
          {product.color && (
            <span className="bg-white text-blue-700 text-xs font-bold px-2 py-1 rounded-full border border-blue-200">
              {product.color}
            </span>
          )}
          {product.size && (
            <span className="bg-white text-cyan-600 text-xs font-bold px-2 py-1 rounded-full border border-cyan-200">
              {product.size}
            </span>
          )}
          {product.voltage && (
            <span className="bg-white text-yellow-600 text-xs font-bold px-2 py-1 rounded-full border border-yellow-200">
              {product.voltage}
            </span>
          )}
          {product.airflow && (
            <span className="bg-white text-gray-600 text-xs font-bold px-2 py-1 rounded-full border border-gray-200">
              {product.airflow}
            </span>
          )}
          {product.material && (
            <span className="bg-white text-orange-600 text-xs font-bold px-2 py-1 rounded-full border border-orange-200">
              {product.material}
            </span>
          )}
          {product.bocas && (
            <span className="bg-white text-red-600 text-xs font-bold px-2 py-1 rounded-full border border-red-200">
              {product.bocas}
            </span>
          )}
          {product.tipo && (
            <span className="bg-white text-green-600 text-xs font-bold px-2 py-1 rounded-full border border-green-200">
              {product.tipo}
            </span>
          )}
          {product.service && (
            <span className="bg-white text-purple-600 text-xs font-bold px-2 py-1 rounded-full border border-purple-200">
              {product.service}
            </span>
          )}
        </div>

        {/* Savings Badge */}
        {product.savings && (
          <div className="mb-3">
            <span className="bg-green-500 text-white text-sm font-bold px-4 py-1 rounded-full">
              ECONOMIZE R$ {product.savings}
            </span>
          </div>
        )}

        {/* Price */}
        <div className="mb-4">
          {product.originalPrice && (
            <p className="text-gray-400 line-through text-sm">
              R$ {Number(product.originalPrice).toFixed(2)}
            </p>
          )}
          <p className="text-3xl font-black text-gray-800">
            R$ {Number(product.price || 0).toFixed(2)}
          </p>
          <p className="text-sm text-gray-600">
            3x de R$ {installmentPrice}
          </p>
        </div>

        {/* Quantity Counter */}
        <div className="flex items-center justify-center gap-3 mb-3">
          <button
            onClick={decrement}
            className="w-9 h-9 rounded-full bg-white border-2 border-gray-300 flex items-center justify-center font-bold text-gray-700 hover:border-blue-500 hover:text-blue-600 transition-colors"
            data-testid={`qty-minus-${product.id}`}
            aria-label="Diminuir quantidade"
          >
            <Minus size={16} />
          </button>
          <span
            className="text-lg font-bold w-10 text-center select-none"
            data-testid={`qty-value-${product.id}`}
          >
            {quantity}
          </span>
          <button
            onClick={increment}
            className="w-9 h-9 rounded-full bg-white border-2 border-gray-300 flex items-center justify-center font-bold text-gray-700 hover:border-blue-500 hover:text-blue-600 transition-colors"
            data-testid={`qty-plus-${product.id}`}
            aria-label="Aumentar quantidade"
          >
            <Plus size={16} />
          </button>
        </div>

        {/* Buy Button */}
        <button
          onClick={handleAddToCart}
          className="w-full bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600 text-white font-bold py-3 px-6 rounded-full transition-all transform hover:scale-105 shadow-lg flex items-center justify-center gap-2"
          data-testid={`add-to-cart-${product.id}`}
        >
          <ShoppingCart size={20} />
          COMPRAR
        </button>

        {/* View Details Link */}
        <button
          onClick={handleCardClick}
          className="w-full mt-2 text-blue-600 hover:text-blue-700 font-semibold text-sm transition-colors"
          data-testid={`view-details-${product.id}`}
        >
          Ver detalhes →
        </button>
      </div>
    </div>
  );
};

export default ProductCard;
